import { useMemo } from 'react'

import { useTranslate } from 'lib/tolgee'
import { PanelStore, UserStore } from 'stores'
import { analyticsTracker } from 'utils'
import captalizeFirstLetter from 'utils/captalizeFirstLetter'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { BRAND_PLACEHOLDER } from './zai-hero-title.consts'
import { translations } from './zai-hero-title.i18n'
import type { IZaiHeroTitleBrand, IZaiHeroTitleProps, IZaiHeroTitleViewProps } from './zai-hero-title.types'

export const useZaiHeroTitleModel = ({ brandData }: IZaiHeroTitleProps): IZaiHeroTitleViewProps => {
  const t = useTranslate()
  const userName = (Object(UserStore?.userInfo)?.firstName ?? '') as string
  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId

  const { isNewPageBrandOverviewEnabled, brandName, brandId, isVerified } = brandData

  const brandDisplay: IZaiHeroTitleBrand | null = useMemo(() => {
    if (!brandName) return null

    if (isNewPageBrandOverviewEnabled) {
      const url =
        brandId && workspaceId
          ? getRoute(PATHNAMES.WORKSPACE_BRAND_OVERVIEW, { panelId: workspaceId, entityId: brandId, tab: 'overview' })
          : undefined

      return {
        name: captalizeFirstLetter(brandName),
        url,
        isVerified,
      }
    }

    return {
      name: captalizeFirstLetter(brandName),
      url: undefined,
      isVerified: false,
    }
  }, [isNewPageBrandOverviewEnabled, brandName, brandId, isVerified, workspaceId])

  const titleParts = useMemo(() => {
    if (!brandDisplay) return null

    const translatedTitle = t(translations.titleWithBrand(BRAND_PLACEHOLDER))

    if (!translatedTitle.includes(BRAND_PLACEHOLDER)) {
      return { before: translatedTitle, after: '' }
    }

    const [before = '', after = ''] = translatedTitle.split(BRAND_PLACEHOLDER)
    return { before, after }
  }, [brandDisplay, t])

  const fallbackTitle = t(translations.title)

  const handleBrandLinkClick = (brandName: string): void => {
    const { BRAND } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(BRAND.HERO_TITLE_CLICK_BRAND, {
      brandName,
    })
  }

  return {
    brandDisplay,
    titleParts,
    fallbackTitle,
    userName,
    onBrandLinkClick: handleBrandLinkClick,
  }
}
